import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { addIcons } from 'ionicons';
import { sparklesOutline, chatbubblesOutline, helpCircleOutline, personAddOutline } from 'ionicons/icons';
import {
  MessageComponent,
  SendSuggestedRollPayload,
} from '../message/message.component';
import { SuggestedActionEntry } from 'src/app/features/chat/types/extra-data';
import { SendSuggestedMessagePayload } from '../message/message.component';
import { IonSpinner, IonLoading, IonIcon, IonButton } from '@ionic/angular/standalone';
import {
  catchError,
  delay,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { VirtualScrollViewportComponent } from '../../../virtual-scroll/virtual-scroll-viewport.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { Message } from 'src/app/features/chat/types/message';
import { ChatService } from 'src/app/features/chat/services/chat.service';
import { VirtualScrollListDirective } from 'src/app/features/virtual-scroll/virtual-scroll-list.directive';
import { VirtualScrollElementDirective } from 'src/app/features/virtual-scroll/virtual-scroll-element.directive';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { CreditsService } from 'src/app/features/credits/credits.service';
import { StorySetupWizardComponent } from '../story-setup-wizard/story-setup-wizard.component';
import type { StorySetupAnswers, StorySetupWizardPhase } from '../story-setup-wizard/story-setup.types';
import { Character } from 'src/app/features/characters/types/character';

@Component({
  selector: 're-chat-content',
  imports: [
    VirtualScrollListDirective,
    VirtualScrollViewportComponent,
    VirtualScrollElementDirective,
    MessageComponent,
    IonSpinner,
    IonLoading,
    IonIcon,
    IonButton,
    AsyncPipe,
    StorySetupWizardComponent,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './chat-content.component.html',
  styleUrls: ['./chat-content.component.scss'],
})
export class ChatContentComponent implements AfterViewInit {
  chatViewport = viewChild<VirtualScrollViewportComponent<Message, string>>(
    VirtualScrollViewportComponent
  );
  chatService = inject(ChatService);
  private credits = inject(CreditsService);
  // It give more precision to detection of needed scrolling
  scrollElement = input<HTMLElement | null>(null);
  quoteMessage = output<Message>();
  sendSuggestedRoll = output<SendSuggestedRollPayload>();
  suggestedActionClick = output<SuggestedActionEntry>();
  sendSuggestedMessage = output<SendSuggestedMessagePayload>();
  invokeMaster = output<void>();
  invokeLore = output<void>();
  showMasterOverlay = output<void>();
  storySetupCompleted = output<StorySetupAnswers>();
  startAdventure = output<void>();
  preferencesCommitted = output<StorySetupAnswers>();
  characterReady = output<void>();
  retrySetting = output<void>();
  createCharacter = output<void>();

  loadingNewMessages = false;

  destroy$ = inject(DestroyRef);

  constructor() {
    addIcons({ sparklesOutline, chatbubblesOutline, helpCircleOutline, personAddOutline });
  }

  showProfile = output<string>();
  showCharacter = output<number>();

  allowBookmark = input<boolean>(false);
  allowSaveChapter = input<boolean>(false);
  allowCharacters = input<boolean>(true);
  isAiChat = input<boolean>(false);
  isLoreChat = input<boolean>(false);
  usePrivateMessageLayout = input<boolean>(false);
  showStorySetupWizard = input<boolean>(false);
  showStartAdventureCta = input<boolean>(false);
  showSetupWizardOverlay = input<boolean>(false);
  storySetupWizardPhase = input<StorySetupWizardPhase>('preferences');
  showCreateCharacterCta = input(false);
  storySettingBrief = input<string | null>(null);
  savedStorySetupAnswers = input<StorySetupAnswers | null>(null);
  showWaitingForHost = input<boolean>(false);
  showStoryStartingWait = input<boolean>(false);
  showSettingRetryCta = input<boolean>(false);
  currentUserHasCharacter = input<boolean>(false);
  storyId = input<number>(0);
  storyCharacters = input<Character[]>([]);
  storyOwnerNames = input<Map<string, string>>(new Map());
  currentUserId = input<string>('');
  storySetupSubmitting = input<boolean>(false);

  isLatestMasterMessage(message: Message): boolean {
    return (
      this.chatService.latestMasterMessageKey() ===
      this.chatService.trackBy(message)
    );
  }

  showEmptyState = computed(
    () =>
      this.chatService.list().length === 0 &&
      ((this.showStorySetupWizard() && !this.showSetupWizardOverlay()) ||
        this.showCreateCharacterCta() ||
        this.showStartAdventureCta() ||
        this.showWaitingForHost() ||
        this.showStoryStartingWait() ||
        this.showSettingRetryCta() ||
        !this.isAiChat() ||
        this.isLoreChat()),
  );

  /** Banner Master a fondo chat Storia. */
  shouldShowMasterBanner = computed(() => {
    if (
      !this.isAiChat() ||
      this.isLoreChat() ||
      !this.chatService.bottomReached() ||
      this.chatService.masterBusy() ||
      this.chatService.list().length === 0
    ) {
      return false;
    }

    return this.chatViewport()?.isLastElementVisible() ?? false;
  });

  /** Banner Lore a fondo chat Archivio. */
  shouldShowLoreBanner = computed(() => {
    if (
      !this.isLoreChat() ||
      !this.chatService.bottomReached() ||
      this.chatService.masterBusy() ||
      this.chatService.list().length === 0
    ) {
      return false;
    }

    return this.chatViewport()?.isLastElementVisible() ?? false;
  });

  async openMasterAlert() {
    if (this.chatService.masterBusy()) {
      this.showMasterOverlay.emit();
      return;
    }
    const confirmed = await this.credits.confirmAiInvoke({
      kind: 'master',
      billed: this.chatService.hasGameplayMasterMessage(),
    });
    if (confirmed) {
      this.invokeMaster.emit();
    }
  }

  async openLoreAlert() {
    if (this.chatService.masterBusy()) {
      this.showMasterOverlay.emit();
      return;
    }
    const confirmed = await this.credits.confirmAiInvoke({
      kind: 'lore',
      billed: true,
    });
    if (confirmed) {
      this.invokeLore.emit();
    }
  }

  saveBookmark = output<Message>();
  removeBookmark = output<Message>();

  saveChapter = output<Message>();
  removeChapter = output<Message>();

  //FIXME
  scrollToLast(id?: number | string) {
    let statement: Observable<{
      id: string | number | undefined | null;
      localId?: string | number | undefined | null;
    } | null> = id
      ? of({ id, localId: undefined })
      : this.chatService.getLastMessageId();
    statement
      .pipe(
        switchMap((last) => {
          //Esiste almeno un messaggio in chat
          if (last?.id || last?.localId) {
            let lastEl = this.chatViewport()?.getLastElement();
            // se la lista la momento caricata contiene il messaggio
            if (
              last.id
                ? this.chatService.getMessageByIdFromLocalList(last.id)
                : this.chatService.getMessageByIdFromLocalList(last.localId!)
            ) {
              // Se l'elemento a cui voglio andare è l'ultimo elemento della list al momento caricata
              if (
                this.scrollElement() &&
                (lastEl?.id === last.id || lastEl?.localId === last.localId)
              ) {
                this.scrollElement()!.scrollTo({
                  top: this.scrollElement()!.scrollHeight,
                  behavior: 'smooth',
                });
              } else if (lastEl)
                this.chatViewport()?.scrollToElement(lastEl?.id! + '');
              return of();
            }
            // se la lista al momento caricata non contiene il messaggio
            else if (typeof last.id === 'number') {
              this.loadingNewMessages = true;
              this.chatService.bottomReached.set(true);
              return this.chatService
                .loadListOfMessagesAroundIndex(last.id)
                .pipe(
                  tap(() => {
                    // after the load of new list of messages, tell to chatviewport that the list is changed and to scroll to index
                    this.chatViewport()?.changedList();
                    this.chatViewport()?.scrollToElement(last.id + '');
                    this.loadingNewMessages = false;
                  }),
                  catchError(() => {
                    this.loadingNewMessages = false;
                    return of(null);
                  })
                );
            } else return of();
          } else return of();
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe();
  }

  scrollTo(index: number) {
    //if list includes index, scroll to index
    if (this.chatViewport()?.list.some((el) => el.id === index)) {
      this.chatViewport()?.scrollToElement(index + '');
    } else {
      // This Function Emulate creation of a new list of message around index and setting of future scroll point to index
      // then take a callback to call
      this.loadingNewMessages = true;
      this.chatService
        .loadListOfMessagesAroundIndex(+index)
        .pipe(
          tap((messages) => {
            // after the load of new list of messages, tell to chatviewport that the list is changed and to scroll to index
            this.chatViewport()?.changedList();
            this.chatViewport()?.scrollToElement(index + '');
            this.loadingNewMessages = false;
          }),
          catchError(() => {
            this.loadingNewMessages = false;
            return of(null);
          }),
          takeUntilDestroyed(this.destroy$)
        )
        .subscribe();
    }
    timer(0)
      .pipe(
        map(() => {
          return this.chatViewport()?.getElementById(index + '')?.element;
        }),
        filter((el) => !!el),
        tap((el) => el?.classList.add('highlight')),
        delay(1500),
        tap((el) => el?.classList.remove('highlight'))
      )
      .subscribe();
  }

  onNewMessage() {
    return this.chatService.onNewMessage$.pipe(
      filter(() => this.chatService.bottomReached()),
      tap((messages) => {
        if (this.scrollElement()) {
          let bottom =
            this.scrollElement()!.scrollHeight -
            this.scrollElement()!.scrollTop -
            this.scrollElement()!.offsetHeight;
          this.chatViewport()
            ?.loadNextBatchBottom()
            .subscribe(() => {
              bottom < 150
                ? this.scrollToLast(
                    messages.at(-1)?.id ? messages.at(-1)?.id : undefined
                  )
                : null;
            });
        } else {
          this.chatViewport()
            ?.loadNextBatchBottom()
            .subscribe(() => {
              this.scrollToLast(
                messages.at(-1)?.id ? messages.at(-1)?.id : undefined
              );
            });
        }
      })
    );
  }

  onNeMessageFromUser() {
    return this.chatService.onNewMessageFromUser$.pipe(
      tap((message) => {
        if (
          message.localId &&
          this.chatViewport()?.getElementById(message.localId)
        ) {
          this.chatViewport()?.changeKeyAndUpdateElement(
            message.localId,
            message.id + '',
            (el) => {
              el.id = message.id;
              el.text = message.text;
              if (message.image) el.image = message.image;
            }
          );
        }
      }),
      tap((message) => {
        if (this.chatService.bottomReached()) {
          if (this.chatViewport()) {
            this.chatViewport()
              ?.loadNextBatchBottom()
              .subscribe(() => {
                this.scrollToLast(message.id ?? message.localId);
              });
          }
        } else this.scrollToLast(message.id ?? message.localId);
      })
    );
  }

  onUpdateMessage() {
    return this.chatService.onUpdateMessage$.pipe(
      tap((val) => {
        if (val.localId)
          this.chatViewport()?.changeKeyAndUpdateElement(
            val.localId,
            val.id + '',
            (el) => {
              el.text = val.text;
              el.id = val.id;
            }
          );
        this.chatViewport()?.updateElement(val);
      })
    );
  }

  ngAfterViewInit(): void {
    merge(
      this.onNewMessage(),
      this.onUpdateMessage(),
      this.onNeMessageFromUser()
    )
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe();
  }

  quoteMessageHandler(message: Message) {
    this.quoteMessage.emit(message);
  }
}
